/**
 * exercise-edit-applicable-dogs.js
 */

import React from 'react';
import { useWatch, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import { EditorIconSection } from 'components/icon-section/icon-section';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import EditorAccordion from 'components/menu-drawer/editor-accordion';
import FormSectionTitle from 'components/menu-drawer/form-section-title';
import ApplicableDogs from 'components/applicable-dogs';
import { Alert } from '@mui/material';


export default function ExerciseEditApplicableDogs(props) {
  const {
    drawerState,
    translation,
    options,
  } = props;
  const { t } = useTranslation(translation);
  const { getValues } = useFormContext();
  const membersDogsAttributes = options.membersDogsAttributes;
  const totalDogs = membersDogsAttributes.reduce((total, member) => total + member.dogs.length, 0);
  const [applicableNum, setApplicableNum] = React.useState(totalDogs);
  const [applicableDetails, setApplicableDetails] = React.useState([]);

  const workModeValue = useWatch({ name: 'workMode' });
  const detectionOdorIds = useWatch({ name: 'detectionOdorIds' });
  const isControlledNegative = useWatch({ name: 'isControlledNegative' });
  // https://pragmatech-workspace.slack.com/archives/C01JC5JLZFU/p1723073766483629
  const patrolTypeIds = getValues('patrolTypeIds');


  React.useEffect(() => {
    let newApplicableNum = null;
    const newApplicableDetails = membersDogsAttributes.filter(m => m.dogs.length > 0);

    if (workModeValue === Types.WorkMode.DETECTION && detectionOdorIds) {
      newApplicableNum = 0;
      newApplicableDetails.forEach(m => {
        m.dogs.forEach(d => {
          let applies = false;
          if (isControlledNegative && d.detectionAttributes.length > 0) {
            applies = d.detectionAttributes.length > 0;
          } else if (!isControlledNegative) {
            const intersection = d.detectionAttributes.filter(v => detectionOdorIds?.includes(v));
            applies = intersection.length > 0;
          }

          d.appliesToExercise = applies;
          newApplicableNum += applies ? 1 : 0;
        });
      });
    } else if (workModeValue === Types.WorkMode.PATROL) {
      newApplicableNum = 0;
      newApplicableDetails.forEach(m => {
        m.dogs.forEach(d => {
          const intersection = d.patrolAttributes.filter(v => patrolTypeIds?.includes(v));
          const applies = intersection.length === patrolTypeIds?.length;
          d.appliesToExercise = applies;
          newApplicableNum += applies ? 1 : 0;
        });
      });
    }

    setApplicableNum(newApplicableNum);
    setApplicableDetails(newApplicableDetails);

  // patrolTypes stringified instead of directly passed
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membersDogsAttributes, workModeValue, detectionOdorIds, JSON.stringify(patrolTypeIds), isControlledNegative]);


  return (
    <>
      <EditorIconSection
        iconName='applicable-dogs'
        iconMarginTop={12}
        helpProps={{
          translation,
          section: 'applicableDogs'
        }}
        data-testid="applicable-dogs-section"
      >
        <HelpTextItem
          sidebarText={drawerState.sidebarText}
          title={t('applicableDogs.general.helpTitle')}
          text={t('applicableDogs.general.helpInfo')}
        >
          <EditorAccordion
            name='Applicable Dogs'
            summary={
              <FormSectionTitle>
                Applies to {applicableNum} / {totalDogs} dogs at this event
              </FormSectionTitle>}
            detail={<ApplicableDogs handlerList={applicableDetails} />}
          />
          {applicableNum === 0 && <Alert severity="error">No dogs at this event can complete this exercise. Check that you've included the right handlers in the event and that their dogs are configured to complete this type of exercise.</Alert>}
        </HelpTextItem>
      </EditorIconSection>
    </>
  );
}